import { SOURCE_LANGUAGE } from '../constants/languages';
import {
    MAX_RETRIES,
    TRANSLATE_PATH,
    USE_MOCK_TRANSLATION,
} from '../constants/translationConfig';
import { scheduleRequest, sleep } from './requestScheduler';
import { translationClient } from './translationClient';

/**
 * Talks to our backend: POST /translation/v1/translate-text
 *   request : { sourceLanguage, targetLanguage, textData: ["text 1", "text 2", ...] }
 *   response: [ { source, target }, ... ]   (same order as textData)
 *
 * Uses the shared translation client (no login token, no session-expiry redirect).
 */
const isRetryable = (error) => {
    if (!error.response) return true; // network error / timeout
    return [429, 502, 503, 504].includes(error.response.status);
};

const retryAfterMs = (error) => {
    const seconds = Number(error.response?.headers?.['retry-after']);
    return Number.isFinite(seconds) && seconds > 0 ? seconds * 1000 : null;
};

const postWithRetry = async (payload) => {
    let lastError;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
        try {
            const response = await translationClient.post(TRANSLATE_PATH, payload);
            return response.data;
        } catch (error) {
            lastError = error;
            if (!isRetryable(error) || attempt === MAX_RETRIES) break;
            await sleep(retryAfterMs(error) ?? 1000 * 2 ** attempt);
        }
    }
    const detail = lastError?.response?.data?.detail || lastError?.response?.data?.message;
    throw new Error(
        typeof detail === 'string' ? detail : lastError?.message || 'Translation request failed.'
    );
};

/** Keeps the result aligned with the request: one entry per text, `null` when missing. */
const alignResponse = (data, texts) => {
    if (!Array.isArray(data)) throw new Error('Unexpected translation response.');
    return texts.map((_, index) => {
        const target = data[index]?.target;
        return typeof target === 'string' && target.trim() ? target : null;
    });
};

const mockTranslate = async (texts, targetLanguage) => {
    await sleep(150);
    return texts.map((text) => `[${targetLanguage}] ${text}`);
};

/**
 * Translates up to one chunk of texts.
 * @param {string[]} texts
 * @param {string} targetLanguage  e.g. 'ar'
 * @returns {Promise<Array<string|null>>}  translations in the same order (null = no translation)
 */
export const translateBatch = async (texts, targetLanguage) => {
    if (!texts.length) return [];
    if (USE_MOCK_TRANSLATION) return mockTranslate(texts, targetLanguage);

    const payload = {
        sourceLanguage: SOURCE_LANGUAGE,
        targetLanguage,
        textData: texts,
    };
    const data = await scheduleRequest(() => postWithRetry(payload));
    return alignResponse(data, texts);
};
